import React from 'react';
import { X, FileText, File, Loader2, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChatbotUploadedFile } from '@/types/chatbot';
import { useTranslation } from 'react-i18next';

interface FileAttachmentProps {
    files: ChatbotUploadedFile[];
    onDelete: (fileId: string) => void;
    isDeleting?: string | null;
    className?: string;
}

const getFileIcon = (filename: string | undefined) => {
    if (!filename) {
        return <File className="w-4 h-4 text-muted-foreground" />;
    }
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') {
        return <FileText className="w-4 h-4 text-red-500" />;
    }
    return <File className="w-4 h-4 text-blue-500" />;
};

const getStatusBadge = (status: string, t: (key: string) => string) => {
    switch (status) {
        case 'ready':
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    {t('chatbotInterface.fileReady')}
                </span>
            );
        case 'processing':
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {t('chatbotInterface.fileProcessing')}
                </span>
            );
        case 'error':
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    <AlertCircle className="w-3 h-3" />
                    {t('chatbotInterface.fileError')}
                </span>
            );
        default:
            return null;
    }
};

const formatExpiration = (expiresAt: string, t: (key: string) => string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffMs <= 0) {
        return t('chatbotInterface.fileExpired');
    }

    if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
};

export const FileAttachment: React.FC<FileAttachmentProps> = ({
    files,
    onDelete,
    isDeleting,
    className
}) => {
    const { t } = useTranslation();

    if (files.length === 0) {
        return null;
    }

    return (
        <div className={cn("space-y-2", className)}>
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                    {t('chatbotInterface.attachedFiles')} ({files.length}/5)
                </span>
            </div>

            <div className="space-y-1.5">
                {files.map((file, index) => (
                    <div
                        key={file.file_id || 'file-' + index}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg",
                            "bg-muted/50 border border-border/40",
                            "group transition-colors hover:bg-muted/70"
                        )}
                    >
                        {getFileIcon(file.filename)}

                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate" title={file.filename || 'File'}>
                                {file.filename || 'Unnamed file'}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {getStatusBadge(file.status, t)}
                                {file.expires_at && file.status === 'ready' && (
                                    <span className="inline-flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {formatExpiration(file.expires_at, t)}
                                    </span>
                                )}
                            </div>
                        </div>

                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => file.file_id && onDelete(file.file_id)}
                            disabled={isDeleting === file.file_id || !file.file_id}
                            className={cn(
                                "h-7 w-7 p-0 opacity-0 group-hover:opacity-100",
                                "hover:bg-destructive/10 hover:text-destructive",
                                "transition-opacity"
                            )}
                            title={t('chatbotInterface.deleteFile')}
                        >
                            {isDeleting === file.file_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <X className="w-4 h-4" />
                            )}
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface FileUploadProgressProps {
    filename: string;
    progress?: number;
}

export const FileUploadProgress: React.FC<FileUploadProgressProps> = ({
    filename,
    progress
}) => {
    const { t } = useTranslation();

    return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{filename}</p>
                <p className="text-xs text-muted-foreground">
                    {t('chatbotInterface.uploadingFile')}
                    {progress !== undefined && ` ${progress}%`}
                </p>
            </div>
        </div>
    );
};
